'use client';

import type { MissionsGroupVariants } from '@/components/features/gamification/missions/types';
import { SliderWrapper } from '@/components/shared/SliderWrapper';
import { SkeletonChild, SkeletonParent } from '@/components/ui/Skeleton/skeleton';
import { cn } from '@/utils/cn';

interface Props {
  variant: MissionsGroupVariants;
}

interface RenderSlidersParams {
  className?: string;
  count?: number;
};

const WelcomeMissionSkeleton = () => (
  <SkeletonParent className="rounded-2xl w-full min-w-38.5 md:min-w-46 h-18 flex justify-center items-center gap-2 md:h-24">
    <SkeletonChild className="h-14 w-16 md:block hidden rounded-xl" />
    <div className="flex-col gap-1 md:flex hidden">
      <SkeletonChild className="h-5 w-20 rounded-lg" />
      <SkeletonChild className="h-4 w-15 rounded-lg" />
    </div>
  </SkeletonParent>
);

const StandartMissionSkeleton = ({ className }: { className?: string }) => (
  <SkeletonParent className={cn('rounded-2xl gap-3 md:gap-4 md:pr-10 px-4 pr-6 w-full flex items-center', className)}>
    <div className="flex flex-col w-full gap-2 md:gap-4">
      <SkeletonChild className="h-3.5 max-md:max-w-40 md:h-4 w-full rounded-lg" />
      <SkeletonChild className="w-full md:hidden block max-w-50 rounded-sm h-2 mt-0.5" />
      <SkeletonChild className="h-3 rounded-full w-15 md:w-[81.45%]" />
    </div>
    <SkeletonChild className="rounded-full shrink-0 h-12.5 w-12.5" />
  </SkeletonParent>
);

export const GmMissionsSkeleton = ({ variant }: Props) => {
  const isWelcome = variant === 'welcome';
  const isDaily = variant === 'daily';

  const Wrapper = isWelcome ? SliderWrapper : 'div';

  const renderSliders = (params?: RenderSlidersParams) => {
    const { className, count } = params ?? {};

    return (
      <div
        className={
          cn('flex w-max lg:w-full', isWelcome
            ? 'max-lg:min-w-[stretch] gap-3 grow flex-1 px-4 lg:px-0'
            : 'md:flex-row w-full flex-col md:gap-3 gap-2', isDaily && 'md:grid grid-cols-3', className)
        }
      >
        {
          Array.from({ length: count || (isDaily ? 6 : 3) }).map((_, i) => {
            if (isWelcome) {
              return <WelcomeMissionSkeleton key={i} />;
            }

            return (
              <StandartMissionSkeleton
                key={i}
                className={cn(
                  isDaily && i < 3 ? 'md:h-34 h-22' : 'md:h-24 h-22',
                )}
              />
            );
          })
        }
      </div>
    );
  };

  const getCountSkeletonsMissions = () => {
    switch (variant) {
      case 'fast': return 1;
      case 'daily': return 5;
      default: return 2;
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <SkeletonParent className="rounded-lg w-fit">
        <SkeletonChild className="rounded-[inherit] w-[5.813rem] h-4 md:h-5.5 md:w-29.5" />
      </SkeletonParent>

      <div
        className={cn('relative', isWelcome && 'max-lg:-mx-4 max-lg:overflow-x-clip lg:px-10 lg:-mx-10')}
      >

        <Wrapper className="max-lg:py-3 max-lg:-my-3 pt-2.5 -mt-2.5">
          {renderSliders({ className: cn('hidden', isDaily ? 'md:grid' : 'md:flex') })}
          {
            renderSliders({
              className: 'md:hidden flex',
              count: getCountSkeletonsMissions(),
            })
          }
        </Wrapper>

      </div>
    </div>
  );
};
